import React, { useState, useRef, useEffect } from "react"
import classNames from "classnames"
import { Swiper, SwiperSlide } from "swiper/react"
import CardContentVideo from "./card-content-video"
import CardContentImage from "./card-content-image"
import { useStaticQuery, graphql } from "gatsby"

const getImage = (name) => {
  const { allFile } = useStaticQuery(
    graphql`
      query {
        allFile(filter: { relativeDirectory: { eq: "images/works" } }) {
          edges {
            node {
              name
              childImageSharp {
                fluid(maxWidth: 800) {
                  ...GatsbyImageSharpFluid
                }
              }
            }
          }
        }
      }
    `
  )
  const imageNode = allFile.edges.find((image) => image.node.name === name)
  if (!imageNode) return null
  return imageNode.node.childImageSharp
}

const SwiperSlideContent = ({ type, source, content }) => {
  if (type === "video") return <CardContentVideo id={source}></CardContentVideo>
  if (type === "image") {
    const image = getImage(source)
    return <CardContentImage {...image}></CardContentImage>
  }
  return null
}

const SwiperNavigation = ({
  slides = [],
  activeIndex = 0,
  onSlideTo = () => {},
}) => {
  if (!slides.length) return null

  return (
    <div className="absolute z-10 top-0 right-0 -mb-px font-charcoal">
      <ul className="flex flex-col justify-end py-8 pr-2">
        {slides.map((_, index) => {
          const isActivatedSlide = activeIndex === index
          return (
            <li
              key={index}
              className={classNames({
                "text-right w-4 h-4 mb-2 rounded-full border border-charcoal shadow-inner": true,
                "transition-background duration-200 ease-out": true,
                "bg-baby-powder": !isActivatedSlide,
                "bg-charcoal": isActivatedSlide,
              })}
              onClick={() => onSlideTo(index)}
            ></li>
          )
        })}
      </ul>
    </div>
  )
}

export default ({ contents, project, order }) => {
  const [swiper, setSwiper] = useState(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [content, setContent] = useState(contents[0])
  const cardEl = useRef(null)

  let cardCarousel = null
  let cardNavigation = null
  let cardNavigationContent = null

  const setCardActive = (isActive, eventName = "") => {
    if (!cardEl || !cardCarousel || !cardNavigation || !cardNavigationContent)
      return

    const offsetRight = 40
    const offsetBottom =
      cardNavigationContent?.clientHeight >= 60
        ? cardNavigationContent.clientHeight
        : 60
    const hasActive = cardEl.current.classList.contains("card--active")

    if (!isActive && hasActive) {
      if (cardCarousel) cardCarousel.style.cssText = null
      cardEl.current.classList.remove("card--active")
      return
    }
    if (isActive && !hasActive) {
      cardEl.current.classList.add("card--active")
      cardCarousel.style.cssText = `bottom: ${offsetBottom}px; right: ${offsetRight}px;`
      return
    }
  }

  const styleSwiperContainer = {
    width: "100%",
    height: "100%",
  }

  const eventOnSlideChange = (Swiper) => {
    setSwiper(Swiper)
    setActiveIndex(Swiper.activeIndex)
    setCardActive(true, "onSlideChange")
  }

  const onSwiperInit = (Swiper) => {
    setSwiper(Swiper)
    setActiveIndex(Swiper.activeIndex)
    Swiper.on("slideChange", eventOnSlideChange)
  }

  const handleSlideTo = (index) => {
    swiper.slideTo(index)
  }

  useEffect(() => {
    setContent(contents[activeIndex])
  }, [activeIndex])

  useEffect(() => {
    cardCarousel = cardEl.current?.children?.namedItem("cardCarousel")
    cardNavigation = cardEl.current?.children?.namedItem("cardNavigation")
    cardNavigationContent = cardNavigation.children.namedItem(
      "cardNavigationContent"
    )

    const eventClick = () => {
      const hasActive = cardEl.current.classList.contains("card--active")
      if (hasActive) return setCardActive(false, "onClick")
      return setCardActive(true, "onClick")
    }
    const eventObserverOn = () => {
      setCardActive(true, "observerOn")
    }
    const eventObserverOff = () => {
      setCardActive(false, "observerOff")
    }
    cardEl.current.addEventListener("observerOn", eventObserverOn)
    cardEl.current.addEventListener("observerOff", eventObserverOff)
    cardCarousel.addEventListener("click", eventClick)

    return () => {
      const opt = { passive: true }
      cardCarousel.removeEventListener("observerOn", eventObserverOn, opt)
      cardCarousel.removeEventListener("observerOff", eventObserverOff, opt)
    }
  }, [])

  return (
    <article data-order={order}>
      <div className="card mb-32 lg:mb-48 xl:mb-64" ref={cardEl} id="card">
        <div
          className="card--carousel transition-all duration-300 ease-in-out -z-1"
          id="cardCarousel"
        >
          <Swiper
            style={styleSwiperContainer}
            onSwiper={onSwiperInit}
            slidesPerView="auto"
            direction="vertical"
            mousewheel={true}
            slidesPerView={1}
            speed={100}
            cssMode
          >
            {contents.map((item, index) => (
              <SwiperSlide key={index}>
                <SwiperSlideContent {...item}></SwiperSlideContent>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
        <div className="card--navigation" id="cardNavigation">
          <SwiperNavigation
            slides={swiper?.slides || []}
            activeIndex={activeIndex}
            onSlideTo={handleSlideTo}
          ></SwiperNavigation>
          <div
            className="absolute bottom-0 left-0 font-extrabold px-2 py-2 md:px-4 lg:px-8 text-sm md:text-base lg:text-xl xl:text-2xl"
            id="cardNavigationContent"
          >
            {content.text}
          </div>
        </div>
        <div className="card--title" id="cardTitle">
          <a
            className="absolute bottom-2 md:bottom-3 lg:bottom-4 right-4 link text-right font-heading text-xs md:text-base lg:text-2xl xl:text-4xl text-baby-powder"
            href={project.url}
            target="_black"
          >
            {project.title}
          </a>
        </div>
      </div>
    </article>
  )
}
